import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { useLoginMutation } from '@/features/auth/api';
import { loginSchema, type LoginInput } from '@/features/auth/schema';

interface FromState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useLoginMutation();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = (input: LoginInput) => {
    loginMutation.mutate(input, {
      onSuccess: () => {
        toast.success('Welcome back');
        const from = (location.state as FromState | undefined)?.from?.pathname ?? '/';
        navigate(from, { replace: true });
      },
      onError: (err) => {
        const message = readErrorMessage(err) ?? 'Invalid credentials. Please try again.';
        form.setError('password', { message });
      },
    });
  };

  const submitDisabled = loginMutation.isPending;

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to your account to continue."
    >
      <Form {...form}>
        <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Username
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    autoComplete="username"
                    autoFocus
                    placeholder="you@company.com"
                    disabled={submitDisabled}
                    className="h-11"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Password
                  </FormLabel>
                  <Link
                    to="/reset-password"
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      disabled={submitDisabled}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={submitDisabled}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {loginMutation.isError && !form.formState.errors.password && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{readErrorMessage(loginMutation.error) ?? 'Could not sign you in.'}</p>
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full h-11 text-sm font-medium"
            disabled={submitDisabled}
          >
            {submitDisabled && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitDisabled ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Form>

      <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
        By signing in you agree to the{' '}
        <a className="font-medium hover:text-foreground" href="#">Terms of Service</a>{' '}
        and{' '}
        <a className="font-medium hover:text-foreground" href="#">Privacy Policy</a>.
      </p>
    </AuthLayout>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (typeof err === 'object' && err !== null) {
    const maybeAxios = err as { response?: { data?: { message?: string } }; message?: string };
    return maybeAxios.response?.data?.message ?? maybeAxios.message;
  }
  if (typeof err === 'string') return err;
  return undefined;
}
